// Endereços, rotas e contatos: abre o Google Maps, o Waze ou o Mapas da Apple com a rota até o endereço,
// liga, chama no WhatsApp, manda e-mail. O app preferido de mapas fica em Ajustes (ou o app pergunta).
import { Alert, Linking, Platform } from 'react-native';
import { getDoc, setDoc } from '../store/store';
import { semBloqueio } from '../auth/AuthContext';
import { paisAparelho } from './moeda';
import { t } from './i18n';

export type AppMapa = 'perguntar' | 'google' | 'waze' | 'apple';
export const appMapa = (): AppMapa => getDoc('ajustes').mapa ?? 'perguntar';
export const setAppMapa = (m: AppMapa) => setDoc('ajustes', d => ({ ...d, mapa: m }));

const enc = encodeURIComponent;
export function urlRota(app: Exclude<AppMapa, 'perguntar'>, destino: string) {
  switch (app) {
    case 'waze': return `https://waze.com/ul?q=${enc(destino)}&navigate=yes`;
    case 'apple': return `https://maps.apple.com/?daddr=${enc(destino)}`;
    default: return `https://www.google.com/maps/dir/?api=1&destination=${enc(destino)}`;
  }
}
export const urlMapa = (destino: string) => `https://www.google.com/maps/search/?api=1&query=${enc(destino)}`;

const abrir = (url: string) => semBloqueio(() => Linking.openURL(url)).catch(() => Alert.alert(t('Não foi possível abrir'), url));

/** Rota até o endereço no app de mapas preferido (ou pergunta qual usar). */
export function abrirRota(destino: string) {
  const d = destino.trim();
  if (!d) return;
  const app = appMapa();
  if (app !== 'perguntar') return abrir(urlRota(app, d));
  const opcoes: { text: string; onPress?: () => void; style?: 'cancel' }[] = [
    { text: 'Google Maps', onPress: () => abrir(urlRota('google', d)) },
    { text: 'Waze', onPress: () => abrir(urlRota('waze', d)) },
  ];
  if (Platform.OS === 'ios') opcoes.unshift({ text: t('Mapas (Apple)'), onPress: () => abrir(urlRota('apple', d)) });
  opcoes.push({ text: t('Cancelar'), style: 'cancel' });
  Alert.alert(t('Como chegar'), d + '\n\n' + t('Para não perguntar mais, escolha o app em Ajustes › Agenda e mapas.'), opcoes);
}

/** Pesquisa um lugar (nome, tipo de lugar ou endereço) no app de mapas, sem iniciar a navegação. */
export function urlBusca(app: Exclude<AppMapa, 'perguntar'>, q: string) {
  switch (app) {
    case 'waze': return `https://waze.com/ul?q=${enc(q)}`;
    case 'apple': return `https://maps.apple.com/?q=${enc(q)}`;
    default: return `https://www.google.com/maps/search/?api=1&query=${enc(q)}`;
  }
}
export function abrirBusca(q: string) {
  const d = q.trim();
  if (!d) return;
  const app = appMapa();
  if (app !== 'perguntar') return abrir(urlBusca(app, d));
  const opcoes: { text: string; onPress?: () => void; style?: 'cancel' }[] = [
    { text: 'Google Maps', onPress: () => abrir(urlBusca('google', d)) },
    { text: 'Waze', onPress: () => abrir(urlBusca('waze', d)) },
  ];
  if (Platform.OS === 'ios') opcoes.unshift({ text: t('Mapas (Apple)'), onPress: () => abrir(urlBusca('apple', d)) });
  opcoes.push({ text: t('Cancelar'), style: 'cancel' });
  Alert.alert(t('Pesquisar no mapa'), d, opcoes);
}
/** Google Maps com a rota passando por vários lugares (até 9 paradas no caminho). */
export function rotaPorTodos(lugares: string[]) {
  const l = lugares.map(x => x.trim()).filter(Boolean);
  if (!l.length) return;
  if (l.length === 1) return abrirRota(l[0]);
  const destino = l[l.length - 1], meio = l.slice(0, -1).slice(0, 9);
  abrir(`https://www.google.com/maps/dir/?api=1&destination=${enc(destino)}&waypoints=${meio.map(enc).join('%7C')}`);
}

// ─── Telefone, WhatsApp, e-mail e site ───
const so = (s: string) => s.replace(/\D/g, '');
/** Número internacional para o WhatsApp: com +, usa como está; no Brasil, sem DDI, põe o 55. */
export function numeroWhats(tel: string) {
  const d = so(tel);
  if (tel.trim().startsWith('+') || d.startsWith('00')) return d.replace(/^00/, '');
  if (paisAparelho() === 'BR' && (d.length === 10 || d.length === 11)) return '55' + d;
  return d;
}
export const ligar = (tel: string) => abrir(`tel:${tel.replace(/[^\d+]/g, '')}`);
export const whatsapp = (tel: string, texto = '') => abrir(`https://wa.me/${numeroWhats(tel)}${texto ? `?text=${enc(texto)}` : ''}`);
export const email = (e: string) => abrir(`mailto:${e.trim()}`);
export const abrirSite = (u: string) => abrir(/^https?:\/\//i.test(u.trim()) ? u.trim() : `https://${u.trim()}`);

export { separarLocal, juntarLocal, localDaTarefa } from './local';
